/**
 * QR Codes Dashboard
 * Overview page with stats, recent QR codes, and quick actions.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack, Title, Text, Group, Card, Button,
  ThemeIcon, SimpleGrid,
} from '@/components/ui';
import { Skeleton } from '@/components/ui';
import { Divider } from '@/components/ui/data-display';
import ActionIcon from '@/components/ui/ActionIcon';
import { FadeIn } from '@/components/animations/transitions';
import {
  IconQrcode, IconScan, IconPlus, IconRefresh,
  IconList, IconActivity, IconCheck, IconCircleOff,
} from '@tabler/icons-react';
import { useCCMS } from '@actschurch/ccms-sdk';
import { QRCODE_QUERY_KEYS } from '../utils/qrCodeConstants';
import RecentQrCodes from './components/RecentQrCodes';

export default function QrCodesDashboard() {
  const navigate = useNavigate();
  const { services } = useCCMS();
  const qrCodeService = services.generators.qrCodeManagement;

  // Fetch stats
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: QRCODE_QUERY_KEYS.stats,
    queryFn: () => qrCodeService.getStats(),
  });

  // Fetch recent QR codes
  const { data: recentCodesResponse, isLoading: codesLoading, refetch: refetchCodes } = useQuery({
    queryKey: QRCODE_QUERY_KEYS.listView,
    queryFn: () => qrCodeService.getFilteredList({ page: 1, pageSize: 10 }),
  });

  // Extract items array from paginated response (API returns { items: [...], totalCount, ... })
  const recentCodes: any[] = Array.isArray(recentCodesResponse)
    ? recentCodesResponse
    : (recentCodesResponse as any)?.items ?? [];

  const isLoading = statsLoading || codesLoading;

  const refetch = () => {
    refetchStats();
    refetchCodes();
  };

  // Derive stat values
  const statValues = useMemo(() => {
    if (!stats) {
      return { total: 0, active: 0, totalScans: 0, scansThisWeek: 0 };
    }
    return {
      total: stats.totalQrCodes ?? stats.total ?? 0,
      active: stats.activeQrCodes ?? stats.active ?? 0,
      totalScans: stats.totalScans ?? 0,
      scansThisWeek: stats.scansThisWeek ?? stats.weeklyScans ?? 0,
    };
  }, [stats]);

  const StatCard = ({
    icon,
    color,
    label,
    value,
    onClick,
  }: {
    icon: React.ReactNode;
    color: string;
    label: string;
    value: number;
    onClick?: () => void;
  }) => (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{ cursor: onClick ? 'pointer' : undefined }}
      onClick={onClick}
    >
      <Group wrap="nowrap">
        <ThemeIcon size={48} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            {label}
          </Text>
          <Text size="xl" fw={700}>
            {value}
          </Text>
        </div>
      </Group>
    </Card>
  );

  if (isLoading) {
    return (
      <FadeIn>
        <Stack gap="lg">
          <SimpleGrid cols={{ base: 2, md: 4 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={90} radius="md" />
            ))}
          </SimpleGrid>
          <Skeleton height={400} radius="md" />
        </Stack>
      </FadeIn>
    );
  }

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <div>
            <Title order={2}>QR Codes Dashboard</Title>
            <Text size="sm" c="dimmed">
              Overview of your QR codes and scan activity
            </Text>
          </div>
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={refetch}>
              <IconRefresh size={18} />
            </ActionIcon>
            <Button
              variant="light"
              leftSection={<IconList size={16} />}
              onClick={() => navigate('/app/qr-codes/list')}
            >
              View All
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate('/app/qr-codes/create')}
            >
              Create QR Code
            </Button>
          </Group>
        </Group>

        {/* Stats Cards */}
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          <StatCard
            icon={<IconQrcode size={24} />}
            color="blue"
            label="Total QR Codes"
            value={statValues.total}
            onClick={() => navigate('/app/qr-codes/list')}
          />
          <StatCard
            icon={<IconCheck size={24} />}
            color="green"
            label="Active"
            value={statValues.active}
          />
          <StatCard
            icon={<IconScan size={24} />}
            color="violet"
            label="Total Scans"
            value={statValues.totalScans}
          />
          <StatCard
            icon={<IconActivity size={24} />}
            color="orange"
            label="Scans This Week"
            value={statValues.scansThisWeek}
          />
        </SimpleGrid>

        {/* Recent QR Codes */}
        <RecentQrCodes codes={recentCodes} />
      </Stack>
    </FadeIn>
  );
}
